import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Button from '@/components/base/button-more'
import Grid from '@/components/base/grid'
import Image from '@/components/base/lazy-image'
import Typography from '@/components/base/typography'
import { useAppStore } from '@/contexts/store'

import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

export type ColumnsCardProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    pretitle: string
    title: string
    paragraph: string
    image: WPImage
    link: string
  }>

const ColumnsCard = ({
  pretitle,
  title,
  paragraph,
  image,
  link,
  className,
  ...props
}: ColumnsCardProps) => {
  const { strings } = useAppStore()
  return (
    <Grid
      className={cn(
        'size-full grid-rows-[auto_1fr] gap-y-[3.6rem]',
        'items-start md:grid-cols-3 lg:grid-cols-6',
        className
      )}
      {...props}
    >
      <div className={cn('col-span-2 md:col-span-3 lg:col-span-6 xl:col-span-3', 'size-full')}>
        <Link
          href={link || '/'}
          scroll={false}
          aria-label={title || 'Apri contenuto'}
          className={cn(
            'relative flex aspect-[16/9] outline-none xl:aspect-[287/365] xl:size-full',
            'before:absolute before:left-0 before:top-0 before:z-10 before:size-full',
            'after:absolute after:left-0 after:top-0 after:size-full after:border-[0rem] after:border-[#EBECEA]',
            'after:transition-all after:duration-300 after:ease-out-cubic hover:after:border-[3rem]',
            'mt-2 after:mix-blend-overlay',
            'focus-visible:before:ring-2 focus-visible:before:ring-[var(--focus-ring-dark)]'
          )}
        >
          <Image src={image?.url || ''} alt="" fill className="object-cover" />
        </Link>
      </div>

      <div
        className={cn(
          'col-span-2 md:col-span-3 lg:col-span-6 xl:col-span-3',
          'grid size-full gap-y-[3.6rem]'
        )}
      >
        <div className="grid gap-y-[3.6rem]">
          {pretitle ? (
            <Typography variant="upper-small" component="h3" className="font-bold uppercase">
              {pretitle}
            </Typography>
          ) : null}

          {title ? (
            <Typography variant="main-title-default" component="h2">
              {title}
            </Typography>
          ) : null}

          {paragraph ? (
            <Typography variant="history-size-body" paragraph className="">
              {paragraph}
            </Typography>
          ) : null}
        </div>

        {link ? (
          <div className="mt-auto flex">
            <Button href={link}>{strings?.labels?.read_more}</Button>
          </div>
        ) : null}
      </div>
    </Grid>
  )
}

export default ColumnsCard
